import type { AuthState } from '../../auth/authStore.ts';
import type { SyncSnapshot } from '../../sync/syncClient.ts';
import { tr } from '../../i18n/i18n.ts';

export type Tone = 'ok' | 'warn' | 'risk' | 'neutral';

/**
 * Synkstatus i klartext. Bygger enbart på verklig data:
 *  - "Synkat" kräver att senaste synk bekräftats av servern och att inget väntar.
 *  - Inloggad säger inget om push, worker eller notisleverans – det visas separat i Notisstatus.
 * Färg bär aldrig betydelsen ensam (texten är alltid med).
 */
const changes = (n: number) => tr(`${n} ändring${n === 1 ? '' : 'ar'}`, `${n} change${n === 1 ? '' : 's'}`);

export type StatusIconKind = 'busy' | 'offline' | 'ok' | 'alert' | 'neutral';

export function syncStatus(auth: AuthState, sync: SyncSnapshot): { label: string; tone: Tone; icon: StatusIconKind } {
  if (auth.status === 'guest') return { label: tr('Ingen synk – data finns bara på den här enheten', 'No sync – data is only on this device'), tone: 'neutral', icon: 'neutral' };
  if (auth.status === 'checking') return { label: tr('Kontrollerar inloggning…', 'Checking sign-in…'), tone: 'neutral', icon: 'busy' };
  if (auth.status === 'expired' || sync.state === 'auth-required') return { label: tr('Sessionen har gått ut', 'Session has expired'), tone: 'risk', icon: 'alert' };
  if (auth.status === 'offline' && auth.reason === 'server')
    return { label: sync.pending > 0 ? tr(`Servern svarar inte just nu – ${changes(sync.pending)} väntar`, `The server is not responding right now – ${changes(sync.pending)} waiting`) : tr('Servern svarar inte just nu', 'The server is not responding right now'), tone: 'warn', icon: 'alert' };
  if (auth.status === 'offline' || sync.state === 'offline') return { label: sync.pending > 0 ? tr(`Offline – ${changes(sync.pending)} väntar`, `Offline – ${changes(sync.pending)} waiting`) : 'Offline', tone: 'warn', icon: 'offline' };
  if (sync.state === 'syncing') return { label: tr('Synkar…', 'Syncing…'), tone: 'neutral', icon: 'busy' };
  if (sync.conflicts.length) return { label: tr(`Synkfel – ${sync.conflicts.length} konflikt${sync.conflicts.length > 1 ? 'er' : ''} att lösa`, `Sync error – ${sync.conflicts.length} conflict${sync.conflicts.length > 1 ? 's' : ''} to resolve`), tone: 'risk', icon: 'alert' };
  if (sync.state === 'error') return { label: tr('Synkfel', 'Sync error'), tone: 'risk', icon: 'alert' };
  if (sync.pending > 0) return { label: tr(`Ändringar väntar (${sync.pending})`, `Changes waiting (${sync.pending})`), tone: 'warn', icon: 'alert' };
  if (sync.state === 'paused') return { label: tr('Synk pausad', 'Sync paused'), tone: 'neutral', icon: 'neutral' };
  if (sync.state === 'idle' && sync.lastSyncAt) return { label: tr('Synkat', 'Synced'), tone: 'ok', icon: 'ok' };
  return { label: tr('Inte synkat ännu', 'Not synced yet'), tone: 'neutral', icon: 'neutral' };
}

export function displayName(name: string | undefined, email: string | undefined): string {
  const n = (name ?? '').trim();
  if (n) return n;
  const local = (email ?? '').split('@')[0]?.trim();
  return local || tr('Ditt konto', 'Your account');
}
